import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const lsKey = (orderId, kind) => `draft:${orderId}:${kind}`;

export function clearDraft(orderId, kind) {
  try { localStorage.removeItem(lsKey(orderId, kind)); } catch {}
  api.delete(`/orders/${orderId}/draft/${kind}`).catch(() => {});
}

// Restores a saved draft once (localStorage first, then server) and autosaves changes (debounce 1s).
export function useDraft({ orderId, kind, value, enabled = true, saveEnabled = true, onRestore }) {
  const ready = useRef(false);
  const last = useRef("");
  useEffect(() => {
    if (!orderId || !enabled) { return undefined; }
    let alive = true;
    const apply = (d) => {
      if (!alive || !d || ready.current) return;
      onRestore(d.data);
      last.current = JSON.stringify(d.data);
      toast.info("Rascunho recuperado");
    };
    let local = null;
    try { local = JSON.parse(localStorage.getItem(lsKey(orderId, kind)) || "null"); } catch {}
    api.get(`/orders/${orderId}/draft/${kind}`).then(({ data }) => {
      const best = [local, data].filter(Boolean).sort((a, b) => (b.at || "").localeCompare(a.at || ""))[0];
      apply(best);
    }).catch(() => apply(local)).finally(() => { if (alive) ready.current = true; });
    return () => { alive = false; };
  }, [orderId, kind, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orderId || !enabled || !saveEnabled || !ready.current) { return undefined; }
    const s = JSON.stringify(value);
    if (s === last.current) { return undefined; }
    const at = new Date().toISOString();
    try { localStorage.setItem(lsKey(orderId, kind), JSON.stringify({ data: value, at })); } catch {}
    const t = setTimeout(() => {
      last.current = s;
      api.put(`/orders/${orderId}/draft/${kind}`, { data: value, at }).catch(() => {});
    }, 1000);
    return () => clearTimeout(t);
  }, [orderId, kind, enabled, saveEnabled, value]);
}

export async function compressImage(file, max = 1600, quality = 0.7) {
  if (!file.type?.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close?.();
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", quality));
    c.width = 0; c.height = 0;
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "foto").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

// Uploads one file at a time (compressed); calls onAdded after each success. Returns files that failed.
export async function uploadPhotos(orderId, files, category, onAdded) {
  const failed = [];
  for (const f of files) {
    try {
      const small = await compressImage(f);
      const fd = new FormData();
      fd.append("file", small);
      const { data } = await api.post(`/orders/${orderId}/photos?category=${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onAdded(data);
    } catch { failed.push(f); }
  }
  return failed;
}
